import { useCallback } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "./useTranslation";

/* English messages the backend (MESSAGES in backend/constant/global.constant.js)
   or a service's own fallback can put in front of the user, mapped to their
   translation keys under `serverMessages`. Developer-only validation messages
   ("email_id is required.") are left out; the forms catch those first. */
const SERVER_MESSAGE_KEYS = {
  // auth
  "Invalid username or password": "invalidLogin",
  "Incorrect username. No account found with this email id.": "invalidUsername",
  "Incorrect password.": "invalidPassword",
  "reCAPTCHA verification is required.": "recaptchaRequired",
  "reCAPTCHA verification failed. Please try again.": "recaptchaFailed",
  "Session expired. Please log in again.": "sessionExpired",
  "Account is no longer active. Please log in again.": "accountInactive",
  Unauthorized: "unauthorized",
  "You do not have permission to perform this action": "forbidden",
  "Login successful": "loginSuccess",

  // users
  "User added successfully": "userAdded",
  "User details updated successfully": "userUpdated",
  "User deleted successfully": "userDeleted",
  "Password reset successfully": "passwordReset",
  "Failed to add user": "addUserFailed",
  "Failed to delete user": "deleteUserFailed",
  "Failed to update user details": "updateUserFailed",
  "User already exists with this email_id.": "userExists",
  "A user with this email already exists": "userExists",
  "User not found": "userNotFound",

  // students
  "Student details updated successfully": "studentUpdated",
  "Student deleted successfully": "studentDeleted",
  "Student already deleted": "studentAlreadyDeleted",
  "Student not found": "studentNotFound",
  "Annotator assigned successfully": "annotatorAssigned",
  "Failed to assign annotator": "assignAnnotatorFailed",
  "Annotator not found": "annotatorNotFound",
  "Delete failed. Please try again.": "deleteFailed",
  "No changes detected": "noChanges",
  "No fields provided to update": "noChanges",

  // samples & exams
  "Samples assign successfully": "samplesAssigned",
  "Sample transferred successfully": "sampleTransferred",
  "Failed to transfer samples": "transferSamplesFailed",
  "Failed to reassign samples": "reassignSamplesFailed",
  "Cannot reassign samples to the same annotator.": "reassignSameAnnotator",
  "You can only reassign samples between annotators you created.": "reassignOwnAnnotatorsOnly",
  "Only an admin can reassign samples.": "adminOnlyReassign",
  "Annotator1 and Annotator2 cannot be the same": "sameAnnotator",
  "Annotator 1 and Annotator 2 cannot be the same.": "sameAnnotator",
  "Provide at least one annotator": "noAnnotatorProvided",
  "No Samples selected": "noSamplesSelected",
  "This exam is already assigned to an annotator.": "examAlreadyAssigned",
  "Assign Annotator 1 before Annotator 2.": "assignAnnotator1First",
  "Cannot save - recording not found or not assigned to you": "recordingNotAssigned",
  "Exam not found": "examNotFound",
  "Annotation saved successfully": "annotationSaved",
  "Exam marked complete for this annotator and statuses updated.": "examMarkedComplete",
  "Remark saved successfully": "remarkSaved",
  "Remark resolved successfully": "remarkResolved",

  // quality check
  "Recordings allotted successfully": "recordingsAllotted",
  "No unallotted recordings left for this CRL": "noRecordingsLeft",
  "Quality check data saved successfully": "qualityCheckSaved",
  "Quality check updated successfully": "qualityCheckSaved",
  "Failed to save quality check": "saveQualityCheckFailed",
  "Some quality check rows were not saved": "someQualityChecksNotSaved",
  "Failed to pull CRL recordings": "pullRecordingsFailed",
  "Recording not found": "recordingNotFound",

  // generic
  "Something went wrong": "somethingWentWrong",
  "Internal server error": "somethingWentWrong",
  // fetch() rejects with this when the server can't be reached
  "Failed to fetch": "connectionFailed",
};

/* Messages with a number in them; `{count}` / `{line}` carry it into the translation. */
const SERVER_MESSAGE_PATTERNS = [
  {
    pattern: /^Complete the quality check of the (\d+) recording\(s\) already pulled/,
    key: "qualityCheckPending",
    param: "count",
  },
  {
    pattern: /^Start time must be earlier than end time \(line (\d+)/,
    key: "timestampOrder",
    param: "line",
  },
  {
    pattern: /^(\w+) must contain English characters only\./,
    key: "englishOnly",
    param: "field",
  },
];

/**
 * Translates a message that came from the server.
 *
 *   const { tServer } = useServerMessage();
 *   showSnackbar("error", tServer(error.message, "admin.x.failed", "Failed to ..."));
 *
 * A known message is shown in the user's language. An unknown one is shown as-is
 * in English; in other languages the translated fallback is shown instead, so a
 * Hindi/Marathi user never gets a raw English sentence.
 */
export const useServerMessage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const tServer = useCallback(
    (message, fallbackKey, fallbackText = "") => {
      const text = typeof message === "string" ? message.trim() : "";

      const key = SERVER_MESSAGE_KEYS[text];
      if (key) return t(`serverMessages.${key}`, text);

      for (const { pattern, key: patternKey, param } of SERVER_MESSAGE_PATTERNS) {
        const match = text.match(pattern);
        if (match) return t(`serverMessages.${patternKey}`, text).replace(`{${param}}`, match[1]);
      }

      const fallback = fallbackKey ? t(fallbackKey, fallbackText) : fallbackText;
      if (!text) return fallback;
      return language === "en" || !fallback ? text : fallback;
    },
    [t, language]
  );

  return { tServer };
};
